import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createWebSocketAdapter, WebSocketAdapter } from '../utils/WebSocketAdapter';
import {
  type AiPromptUsage,
  type CompanyQuestion,
  type PaidRoomEntry,
  type PremiumPurchase,
  type RoomBranding,
} from '../types';
import './Dashboard.css';
import RoomDetail from './RoomDetail';
import QuestionsPage from './QuestionsPage';
import PlayerFeedbackPage from './PlayerFeedbackPage';
import PromoRoomsPage from './PromoRoomsPage';
import PaidRoomsPage from './PaidRoomsPage';
import { countPlayersWhoPlayed } from '../utils/playersWhoPlayed';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  joinedAt: number;
  isHost?: boolean;
  connected?: boolean;
}

interface CurrentQuestion {
  id?: string;
  questionId?: string;
  question: string;
  options?: string[];
  correctAnswer?: string;
  imageUrl?: string;
  questionNumber?: number;
  totalQuestions?: number;
  interesting?: string;
}

interface GameEvent {
  type: 'game_started' | 'game_ended' | 'player_joined' | 'player_left';
  timestamp: number;
  gameType?: string;
  questionNumber?: number;
  totalQuestions?: number;
  reason?: string;
  playerName?: string;
}

interface RoomData {
  roomName: string;
  gameType: string;
  hostId: string;
  players: Player[];
  gameState: 'waiting' | 'playing' | 'finished';
  settings?: {
    timerDuration?: number;
    category?: string;
    whosPlaying?: string;
    numberOfQuestions?: number;
  };
  createdAt?: number;
  gamesPlayed?: number;
  questionsPlayed?: number;
  currentQuestion?: CurrentQuestion;
  questionStartTime?: number;
  gameHistory?: GameEvent[];
  destroyedAt?: number; // For historical rooms
  stateEnteredAt?: number;
  hostDisconnectedAt?: number | null;
  connectedPlayerCount?: number;
  isStalePlaying?: boolean;
  branding?: RoomBranding;
  companyQuestions?: CompanyQuestion[];
  aiPromptUsage?: AiPromptUsage;
}

interface DashboardProps {
  onLogout: () => void;
}

const GAME_TYPE_LABELS: Record<string, string> = {
  'trivia': 'Trivia',
  'speed-challenge': 'Speed Challenge',
  'collaborative-puzzle': 'Collaborative Puzzle',
  'story-chain': 'Story Chain',
  'word-puzzle': 'Word Puzzle',
  'close-or-not': 'Close or Not',
  'picture-perfect': 'Picture Perfect',
  'drinking-game': 'Drinking Game',
  'hot-takes': 'Hot Takes',
  'would-you-rather': 'Would You Rather',
  'odd-one-out': 'Odd One Out',
  'estimate-it': 'Estimate It',
  'true-or-silly': 'True or Silly?',
};

const STATE_COLORS: Record<string, string> = {
  waiting: 'var(--accent-warning)',
  playing: 'var(--accent-primary)',
  finished: 'var(--accent-secondary)',
  stale: 'var(--text-muted, #888)',
};

// Helper to get displayable avatar (handles SVG paths from main app)
const getDisplayAvatar = (avatar?: string): string => {
  if (!avatar) return '👤';
  // If it's a path (starts with / or http or contains .svg), use default emoji
  if (avatar.startsWith('/') || avatar.startsWith('http') || avatar.includes('.svg')) {
    return '👤';
  }
  return avatar;
};

type DashboardView = 'dashboard' | 'questions' | 'player-feedback' | 'promo-rooms' | 'paid-rooms';
type PlayerCountFilter = 1 | 2 | 4 | null;

const PLAYER_COUNT_FILTERS: Array<{ value: 1 | 2 | 4; label: string }> = [
  { value: 1, label: '>1' },
  { value: 2, label: '>2' },
  { value: 4, label: '>4' },
];

function filterRoomsByPlayerCount(rooms: RoomData[], minPlayers: PlayerCountFilter): RoomData[] {
  if (minPlayers === null) return rooms;
  return rooms.filter((room) => countPlayersWhoPlayed(room) > minPlayers);
}

const Dashboard: React.FC<DashboardProps> = ({ onLogout }) => {
  const [socket, setSocket] = useState<WebSocketAdapter | null>(null);
  const [rooms, setRooms] = useState<RoomData[]>([]);
  const [historyRooms, setHistoryRooms] = useState<RoomData[]>([]);
  const [paidRooms, setPaidRooms] = useState<PaidRoomEntry[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<RoomData | null>(null);
  const [selectedPremiumPurchase, setSelectedPremiumPurchase] = useState<PremiumPurchase | null>(null);
  const [view, setView] = useState<DashboardView>('dashboard');
  const [connected, setConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isHistoryLoading, setIsHistoryLoading] = useState(true);
  const [activeRoomsExpanded, setActiveRoomsExpanded] = useState(true);
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [activePlayerFilter, setActivePlayerFilter] = useState<PlayerCountFilter>(null);
  const [historyPlayerFilter, setHistoryPlayerFilter] = useState<PlayerCountFilter>(null);
  const [questionsRefreshToken, setQuestionsRefreshToken] = useState(0);
  const [playerFeedbackRefreshToken, setPlayerFeedbackRefreshToken] = useState(0);
  const [roomDetailRefreshToken, setRoomDetailRefreshToken] = useState(0);

  // Use ref to track selected room name to avoid re-creating socket on selection change
  const selectedRoomNameRef = useRef<string | null>(null);
  // Track if current selection is from paid list so we don't clear it when admin-rooms-data omits it
  const selectedIsPaidRoomRef = useRef<boolean>(false);

  // Keep refs in sync with state
  useEffect(() => {
    selectedRoomNameRef.current = selectedRoom?.roomName || null;
  }, [selectedRoom]);
  useEffect(() => {
    selectedIsPaidRoomRef.current = selectedPremiumPurchase !== null;
  }, [selectedPremiumPurchase]);

  const requestRooms = useCallback((sock: WebSocketAdapter) => {
    setIsRefreshing(true);
    sock.emit('admin-get-rooms', {});
  }, []);

  const requestSingleRoom = useCallback((sock: WebSocketAdapter, roomName: string) => {
    setIsRefreshing(true);
    sock.emit('admin-get-room', { roomName });
  }, []);

  const requestHistory = useCallback((sock: WebSocketAdapter) => {
    setIsHistoryLoading(true);
    sock.emit('admin-get-history', {});
  }, []);

  const requestPaidRooms = useCallback((sock: WebSocketAdapter) => {
    sock.emit('admin-get-paid-rooms', {});
  }, []);

  useEffect(() => {
    const newSocket = createWebSocketAdapter(BACKEND_URL);

    newSocket.on('connect', () => {
      console.log('Admin connected to backend');
      setConnected(true);
      setIsRefreshing(true);
      requestRooms(newSocket);
      requestHistory(newSocket);
      requestPaidRooms(newSocket);
    });

    newSocket.on('disconnect', () => {
      console.log('Admin disconnected from backend');
      setConnected(false);
      setIsHistoryLoading(false);
    });

    newSocket.on('admin-rooms-data', (data: { rooms: RoomData[] }) => {
      setRooms(data.rooms || []);
      setLastUpdate(new Date());
      setIsRefreshing(false);

      // Update selected room if it's still in the list (use ref to avoid closure issues)
      const currentSelectedName = selectedRoomNameRef.current;
      if (currentSelectedName) {
        // Don't clear selection for paid rooms: they come from paid list, not live rooms
        if (selectedIsPaidRoomRef.current) return;
        const updatedRoom = data.rooms.find(r => r.roomName === currentSelectedName);
        if (updatedRoom) {
          setSelectedRoom(updatedRoom);
        } else {
          // Room was destroyed
          setSelectedRoom(null);
        }
      }
    });

    newSocket.on('admin-room-data', (data: { room: RoomData | null; premiumPurchase?: PremiumPurchase }) => {
      setLastUpdate(new Date());
      setIsRefreshing(false);

      if (data.premiumPurchase) setSelectedPremiumPurchase(data.premiumPurchase);

      if (data.room) {
        setSelectedRoom((prev) => {
          // Preserve stub fields when refresh returns a thinner offline stub
          if (
            selectedIsPaidRoomRef.current &&
            prev &&
            prev.roomName === data.room!.roomName &&
            !data.room!.hostId &&
            prev.hostId
          ) {
            return {
              ...prev,
              ...data.room!,
              branding: data.room!.branding ?? prev.branding,
              companyQuestions: data.room!.companyQuestions ?? prev.companyQuestions,
              aiPromptUsage: data.room!.aiPromptUsage ?? prev.aiPromptUsage,
            };
          }
          return data.room!;
        });
        // Also update in rooms list when it's an active room
        setRooms(prev => prev.map(r => r.roomName === data.room!.roomName ? data.room! : r));
      } else {
        // Don't clear selection for paid rooms: backend may return null if room isn't in memory
        if (!selectedIsPaidRoomRef.current) {
          setSelectedRoom(null);
        }
      }
    });

    newSocket.on('admin-history-data', (data: { rooms: RoomData[]; error?: string }) => {
      if (data.error) {
        console.error('[Admin] Error fetching history:', data.error);
      }
      setHistoryRooms(data.rooms || []);
      setIsHistoryLoading(false);
    });

    newSocket.on('admin-paid-rooms-data', (data: { paidRooms: PaidRoomEntry[] }) => {
      setPaidRooms(data.paidRooms || []);
      setLastUpdate(new Date());
      setIsRefreshing(false);

      const currentSelectedName = selectedRoomNameRef.current;
      if (currentSelectedName && selectedIsPaidRoomRef.current) {
        const entry = (data.paidRooms || []).find(
          (e) => e.purchase.roomName === currentSelectedName
        );
        if (entry) {
          setSelectedPremiumPurchase(entry.purchase);
          if (entry.room) {
            setSelectedRoom(entry.room);
          }
        }
      }
    });

    newSocket.on('admin-room-destroyed', (data: { roomName: string; success: boolean }) => {
      if (!data.success) {
        console.warn(`[Admin] Failed to destroy room ${data.roomName}`);
        window.alert(`Could not destroy room ${data.roomName}. It may already be gone — try Refresh.`);
        return;
      }
      setRooms(prev => prev.filter(room => room.roomName !== data.roomName));
      setSelectedRoom(prev => (prev?.roomName === data.roomName ? null : prev));
      setLastUpdate(new Date());
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [requestRooms, requestSingleRoom, requestHistory, requestPaidRooms]);

  const handleRefresh = () => {
    if (socket && socket.connected) {
      setIsRefreshing(true);
      if (view === 'questions') {
        setQuestionsRefreshToken((value) => value + 1);
        window.setTimeout(() => setIsRefreshing(false), 500);
        return;
      }
      if (view === 'player-feedback') {
        setPlayerFeedbackRefreshToken((value) => value + 1);
        window.setTimeout(() => setIsRefreshing(false), 500);
        return;
      }
      const currentSelectedName = selectedRoomNameRef.current;
      if (currentSelectedName) {
        setRoomDetailRefreshToken((value) => value + 1);
        requestSingleRoom(socket, currentSelectedName);
        if (view === 'paid-rooms') requestPaidRooms(socket);
      } else {
        if (view === 'paid-rooms') {
          requestPaidRooms(socket);
        } else {
          requestRooms(socket);
          requestHistory(socket);
          requestPaidRooms(socket);
        }
      }
    }
  };

  const handleRoomClick = (room: RoomData) => {
    selectedIsPaidRoomRef.current = false;
    setSelectedPremiumPurchase(null);
    setSelectedRoom(room);
    if (socket?.connected) {
      requestSingleRoom(socket, room.roomName);
    }
  };

  const handlePaidRoomClick = (entry: PaidRoomEntry) => {
    selectedIsPaidRoomRef.current = true;
    setSelectedPremiumPurchase(entry.purchase);
    if (entry.room) {
      setSelectedRoom(entry.room);
    } else {
      setSelectedRoom({
        roomName: entry.purchase.roomName,
        gameType: '',
        hostId: '',
        players: [],
        gameState: 'waiting',
      });
      if (socket?.connected) {
        requestSingleRoom(socket, entry.purchase.roomName);
      }
    }
  };

  const handleBackToList = () => {
    selectedIsPaidRoomRef.current = false;
    setSelectedRoom(null);
    setSelectedPremiumPurchase(null);
  };

  const switchView = (next: DashboardView) => {
    handleBackToList();
    setView(next);
  };

  const handleKillRoom = (roomName: string) => {
    if (socket && socket.connected) {
      socket.emit('admin-destroy-room', { roomName });
    }
  };

  const filteredActiveRooms = filterRoomsByPlayerCount(rooms, activePlayerFilter);
  const filteredHistoryRooms = filterRoomsByPlayerCount(historyRooms, historyPlayerFilter);

  const totalPlayers = rooms.reduce((sum, room) => {
    if (typeof room.connectedPlayerCount === 'number') {
      return sum + room.connectedPlayerCount;
    }
    return sum + room.players.filter((p) => p.connected !== false).length;
  }, 0);
  const activeGames = rooms.filter(
    (r) => r.gameState === 'playing' && !r.isStalePlaying
  ).length;
  const stalePlaying = rooms.filter((r) => r.isStalePlaying).length;

  // Set of room names that have a premium purchase (for Paid/Free chip on Dashboard)
  const paidRoomNames = new Set(paidRooms.map((e) => e.purchase.roomName));

  const renderPlayerCountFilters = (
    currentFilter: PlayerCountFilter,
    onChange: (filter: PlayerCountFilter) => void
  ) => (
    <div className="player-count-filters" onClick={(e) => e.stopPropagation()}>
      {PLAYER_COUNT_FILTERS.map(({ value, label }) => (
        <button
          key={value}
          type="button"
          className={`player-count-filter ${currentFilter === value ? 'active' : ''}`}
          onClick={() => onChange(currentFilter === value ? null : value)}
          aria-pressed={currentFilter === value}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <h1 className="header-title">
            <span className="title-accent">brainz</span>
            <span className="title-dot">.</span>
            admin
          </h1>
          <nav className="dashboard-nav">
            <button
              className={`nav-tab ${view === 'dashboard' ? 'active' : ''}`}
              onClick={() => switchView('dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`nav-tab ${view === 'paid-rooms' ? 'active' : ''}`}
              onClick={() => switchView('paid-rooms')}
            >
              Paid Rooms
            </button>
            <button
              className={`nav-tab ${view === 'promo-rooms' ? 'active' : ''}`}
              onClick={() => switchView('promo-rooms')}
            >
              Promo Rooms
            </button>
            <button
              className={`nav-tab ${view === 'questions' ? 'active' : ''}`}
              onClick={() => switchView('questions')}
            >
              Questions
            </button>
            <button
              className={`nav-tab ${view === 'player-feedback' ? 'active' : ''}`}
              onClick={() => switchView('player-feedback')}
            >
              Player Feedback
            </button>
          </nav>
          <div className="connection-status">
            <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`} />
            <span className="status-text">{connected ? 'LIVE' : 'OFFLINE'}</span>
          </div>
        </div>
        <div className="header-right">
          <button className="refresh-button" onClick={handleRefresh} disabled={isRefreshing}>
            <span className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}>↻</span>
            {isRefreshing ? 'Syncing...' : 'Refresh'}
          </button>
          <button className="logout-button" onClick={onLogout}>
            Logout
            <span className="logout-icon">⏻</span>
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        {view === 'questions' ? (
          <QuestionsPage socket={socket} refreshToken={questionsRefreshToken} />
        ) : view === 'player-feedback' ? (
          <PlayerFeedbackPage socket={socket} refreshToken={playerFeedbackRefreshToken} />
        ) : view === 'promo-rooms' ? (
          selectedRoom ? (
            <RoomDetail
              room={selectedRoom}
              premiumPurchase={selectedPremiumPurchase}
              onBack={handleBackToList}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              onKillRoom={handleKillRoom}
              socket={socket}
              refreshToken={roomDetailRefreshToken}
            />
          ) : (
            <PromoRoomsPage
              socket={socket}
              paidRooms={paidRooms}
              onRoomClick={handlePaidRoomClick}
              onRefresh={() => {
                if (socket?.connected) {
                  setIsRefreshing(true);
                  requestPaidRooms(socket);
                }
              }}
            />
          )
        ) : view === 'paid-rooms' ? (
          selectedRoom ? (
            <RoomDetail
              room={selectedRoom}
              premiumPurchase={selectedPremiumPurchase}
              onBack={handleBackToList}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
              onKillRoom={handleKillRoom}
              socket={socket}
              refreshToken={roomDetailRefreshToken}
            />
          ) : (
            <PaidRoomsPage
              paidRooms={paidRooms}
              onRoomClick={handlePaidRoomClick}
            />
          )
        ) : selectedRoom ? (
          <RoomDetail
            room={selectedRoom}
            premiumPurchase={selectedPremiumPurchase}
            onBack={handleBackToList}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            onKillRoom={handleKillRoom}
            socket={socket}
            refreshToken={roomDetailRefreshToken}
          />
        ) : (
          <>
            {/* Stats Bar */}
            <div className="stats-bar">
              <div className="stat-card">
                <div className="stat-value">{rooms.length}</div>
                <div className="stat-label">Active Rooms</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totalPlayers}</div>
                <div className="stat-label">Players Online</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{activeGames}</div>
                <div className="stat-label">Games in Progress</div>
              </div>
              {stalePlaying > 0 && (
                <div className="stat-card">
                  <div className="stat-value" style={{ color: STATE_COLORS.stale }}>
                    {stalePlaying}
                  </div>
                  <div className="stat-label">Stale Playing</div>
                </div>
              )}
              <div className="stat-card time-card">
                <div className="stat-value small">
                  {lastUpdate ? lastUpdate.toLocaleTimeString() : '--:--:--'}
                </div>
                <div className="stat-label">Last Update</div>
              </div>
            </div>

            {/* Rooms Grid */}
            <section className="rooms-section">
              <div
                className="section-header section-header-toggle"
                onClick={() => setActiveRoomsExpanded((e) => !e)}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => ev.key === 'Enter' && setActiveRoomsExpanded((e) => !e)}
                aria-expanded={activeRoomsExpanded}
              >
                <div className="section-header-left">
                  <h2 className="section-title">
                    <span className="title-prefix">&gt;</span> Active Rooms
                    <span className={`section-chevron ${activeRoomsExpanded ? 'expanded' : ''}`}>▾</span>
                  </h2>
                  {renderPlayerCountFilters(activePlayerFilter, setActivePlayerFilter)}
                </div>
                <span className="room-count">
                  {filteredActiveRooms.length} room{filteredActiveRooms.length !== 1 ? 's' : ''}
                  {activePlayerFilter !== null && rooms.length !== filteredActiveRooms.length
                    ? ` / ${rooms.length}`
                    : ''}
                </span>
              </div>

              {activeRoomsExpanded && (rooms.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">🎮</div>
                  <p className="empty-text">No active rooms</p>
                  <p className="empty-subtext">Rooms will appear here when players create them</p>
                </div>
              ) : filteredActiveRooms.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👥</div>
                  <p className="empty-text">No rooms match this player filter</p>
                  <p className="empty-subtext">Try a different filter or clear the selection</p>
                </div>
              ) : (
                <div className="rooms-grid">
                  {filteredActiveRooms.map((room, index) => {
                    const isPaid = paidRoomNames.has(room.roomName);
                    const createdLabel = room.createdAt
                      ? getTimeAgo(new Date(room.createdAt))
                      : null;
                    const onlineCount =
                      typeof room.connectedPlayerCount === 'number'
                        ? room.connectedPlayerCount
                        : room.players.filter((p) => p.connected !== false).length;
                    const stateLabel = room.isStalePlaying
                      ? 'STALE'
                      : room.gameState.toUpperCase();
                    const stateColor = room.isStalePlaying
                      ? STATE_COLORS.stale
                      : STATE_COLORS[room.gameState];
                    return (
                    <div
                      key={room.roomName}
                      className={`room-card${room.isStalePlaying ? ' room-card--stale' : ''}`}
                      onClick={() => handleRoomClick(room)}
                      style={{ animationDelay: `${index * 0.05}s` }}
                    >
                      <div className="room-card-header">
                        <span className="room-code">{room.roomName}</span>
                        <div className="room-card-header-chips">
                          <span
                            className={`room-type-chip ${isPaid ? 'paid' : 'free'}`}
                          >
                            {isPaid ? 'Paid' : 'Free'}
                          </span>
                          <span
                            className="room-state"
                            style={{ color: stateColor }}
                            title={
                              room.isStalePlaying
                                ? 'Abandoned mid-game (host gone, nobody online) — will auto-kill after 1h'
                                : undefined
                            }
                          >
                            {stateLabel}
                          </span>
                        </div>
                      </div>

                      <div className="room-card-body">
                        <div className="game-type">
                          {GAME_TYPE_LABELS[room.gameType] || room.gameType}
                        </div>
                        <div className="player-count">
                          <span className="player-icon">👥</span>
                          <span className="player-number">{onlineCount}</span>
                          <span className="player-label">
                            online
                            {room.players.length !== onlineCount
                              ? ` / ${room.players.length} listed`
                              : room.players.length === 1
                                ? ' player'
                                : ' players'}
                          </span>
                        </div>
                        {room.hostDisconnectedAt != null && (
                          <div className="room-created" style={{ color: STATE_COLORS.stale }}>
                            Host disconnected
                          </div>
                        )}
                        {createdLabel && (
                          <div className="room-created">
                            Created {createdLabel}
                          </div>
                        )}
                      </div>

                      <div className="room-card-footer">
                        <div className="player-avatars">
                          {room.players.slice(0, 4).map((player, i) => (
                            <span key={player.id} className="avatar" style={{ zIndex: 4 - i }}>
                              {getDisplayAvatar(player.avatar)}
                            </span>
                          ))}
                          {room.players.length > 4 && (
                            <span className="avatar more">+{room.players.length - 4}</span>
                          )}
                        </div>
                        <span className="view-details">View →</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              ))}
            </section>

            {/* Past destroyed rooms (KV retention matches backend) */}
            <section className="rooms-section rooms-section-spaced">
              <div
                className="section-header section-header-toggle"
                onClick={() => setHistoryExpanded((e) => !e)}
                role="button"
                tabIndex={0}
                onKeyDown={(ev) => ev.key === 'Enter' && setHistoryExpanded((e) => !e)}
                aria-expanded={historyExpanded}
              >
                <div className="section-header-left">
                  <h2 className="section-title">
                    <span className="title-prefix">&gt;</span> Past week
                    <span className={`section-chevron ${historyExpanded ? 'expanded' : ''}`}>▾</span>
                  </h2>
                  {renderPlayerCountFilters(historyPlayerFilter, setHistoryPlayerFilter)}
                </div>
                <span className="room-count">
                  {isHistoryLoading
                    ? '…'
                    : `${filteredHistoryRooms.length} room${filteredHistoryRooms.length !== 1 ? 's' : ''}${
                        historyPlayerFilter !== null && historyRooms.length !== filteredHistoryRooms.length
                          ? ` / ${historyRooms.length}`
                          : ''
                      }`}
                </span>
              </div>

              {historyExpanded && (isHistoryLoading ? (
                <div className="empty-state">
                  <div className="section-loading-spinner" aria-hidden="true" />
                  <p className="empty-text">Loading room history…</p>
                  <p className="empty-subtext">Fetching destroyed rooms from the last 7 days</p>
                </div>
              ) : historyRooms.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">📜</div>
                  <p className="empty-text">No room history</p>
                  <p className="empty-subtext">Destroyed rooms from the last 7 days appear here</p>
                </div>
              ) : filteredHistoryRooms.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👥</div>
                  <p className="empty-text">No rooms match this player filter</p>
                  <p className="empty-subtext">Try a different filter or clear the selection</p>
                </div>
              ) : (
                <div className="rooms-grid">
                  {filteredHistoryRooms.map((room, index) => {
                    const destroyedDate = room.destroyedAt ? new Date(room.destroyedAt) : null;
                    const timeAgo = destroyedDate ? getTimeAgo(destroyedDate) : 'Unknown';
                    const playersWhoPlayed = countPlayersWhoPlayed(room);
                    return (
                      <div
                        key={`${room.roomName}-${room.destroyedAt}`}
                        className="room-card history-card"
                        onClick={() => handleRoomClick(room)}
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="room-card-header">
                          <span className="room-code">{room.roomName}</span>
                          <div className="room-card-header-chips">
                            <span
                              className={`room-type-chip ${paidRoomNames.has(room.roomName) ? 'paid' : 'free'}`}
                            >
                              {paidRoomNames.has(room.roomName) ? 'Paid' : 'Free'}
                            </span>
                            <span className="room-time-ago">{timeAgo}</span>
                          </div>
                        </div>

                        <div className="room-card-body">
                          <div className="game-type">
                            {GAME_TYPE_LABELS[room.gameType] || room.gameType}
                          </div>
                          <div className="player-count">
                            <span className="player-icon">👥</span>
                            <span className="player-number">{playersWhoPlayed}</span>
                            <span className="player-label">
                              player{playersWhoPlayed !== 1 ? 's' : ''}
                            </span>
                            {room.gamesPlayed !== undefined && (
                              <>
                                <span className="separator">•</span>
                                <span className="games-played">{room.gamesPlayed} game{room.gamesPlayed !== 1 ? 's' : ''}</span>
                              </>
                            )}
                            {room.questionsPlayed != null && room.questionsPlayed > 0 && (
                              <>
                                <span className="separator">•</span>
                                <span className="games-played">
                                  {room.questionsPlayed} question{room.questionsPlayed !== 1 ? 's' : ''}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="room-card-footer">
                          <div className="player-avatars">
                            {room.players.slice(0, 4).map((player, i) => (
                              <span key={player.id} className="avatar" style={{ zIndex: 4 - i }}>
                                {getDisplayAvatar(player.avatar)}
                              </span>
                            ))}
                            {room.players.length > 4 && (
                              <span className="avatar more">+{room.players.length - 4}</span>
                            )}
                          </div>
                          <span className="view-details">View →</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

// Helper function to format time ago
function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 2) {
    return '1d ago';
  } else {
    return `${diffDays}d ago`;
  }
}

export default Dashboard;
